/**
 * Cotação de deslocamento no ticket (pedido da PoolTrack, 04/10; escopo
 * revisto pelo operador no mesmo dia).
 *
 * Duas pernas, cada uma com o próprio km e o próprio preço por km DIGITADO por
 * quem cota — nenhum preço vem de cadastro:
 *
 *   - ida:   origem (de onde o técnico sai) → destino (o endereço do serviço);
 *   - volta: destino → ponto de retorno (padrão: a própria origem).
 *
 * total = km_ida × preço_ida + km_volta × preço_volta, em centavos INTEIROS
 * (chargeTotalCents, a mesma regra dos lançamentos). O km de cada perna guarda
 * os METROS exatos do provedor e é cobrado com UMA casa, arredondado meio para
 * cima — a mesma conta no servidor, na tela e no lançamento.
 *
 * Puro, sem dependência além do zod: roda na API e no SPA.
 */
import { z } from 'zod';
import { chargeTotalCents } from './billing';
import { Dateish } from './common';

export const QUOTE_POINTS = ['origin', 'destination', 'returnTo'] as const;
export type QuotePointKey = (typeof QUOTE_POINTS)[number];

export const QUOTE_LEGS = ['ida', 'volta'] as const;
export type QuoteLegKey = (typeof QUOTE_LEGS)[number];

export const QUOTE_LEG_LABELS: Record<QuoteLegKey, string> = { ida: 'Ida', volta: 'Volta' };

/** quem calculou a rota da perna; `manual` = km informado pela pessoa, com motivo */
export const QUOTE_LEG_PROVIDERS = ['google', 'fake', 'manual'] as const;
export type QuoteLegProvider = (typeof QUOTE_LEG_PROVIDERS)[number];
export type QuoteRouteProvider = Exclude<QuoteLegProvider, 'manual'>;

/**
 * Precisão do ponto. As quatro primeiras são a `granularity` do geocodificador
 * (ROOFTOP, RANGE_INTERPOLATED, GEOMETRIC_CENTER, APPROXIMATE); `pin` é o ponto
 * que a pessoa posicionou arrastando o marcador no mapa — a posição é dela, não
 * do geocodificador.
 */
export const GEO_PRECISIONS = ['exact', 'interpolated', 'center', 'approximate', 'pin'] as const;
export type GeoPrecision = (typeof GEO_PRECISIONS)[number];

export const GEO_PRECISION_LABELS: Record<GeoPrecision, string> = {
  exact: 'número exato',
  interpolated: 'número estimado na rua',
  center: 'centro da rua ou do bairro',
  approximate: 'aproximado',
  pin: 'posicionado no mapa',
};

/**
 * Avisos de um ponto — o preço depende da posição, então o que a deixa incerta
 * aparece para quem cota:
 *
 * - `approximate`: o geocodificador só achou o centro de uma rua/bairro/cidade;
 * - `city_not_in_text`: a cidade encontrada não aparece no texto digitado (ele
 *   pode ter escolhido um homônimo em outra cidade);
 * - `several_matches`: mais de um endereço no Brasil casou com o texto — o
 *   primeiro foi usado.
 */
export const QUOTE_POINT_WARNINGS = ['approximate', 'city_not_in_text', 'several_matches'] as const;
export type QuotePointWarning = (typeof QUOTE_POINT_WARNINGS)[number];

export const QUOTE_POINT_WARNING_LABELS: Record<QuotePointWarning, string> = {
  approximate: 'Posição aproximada: confira no mapa e arraste o marcador se precisar.',
  city_not_in_text:
    'A cidade encontrada não está no endereço digitado: confira se é a cidade certa.',
  several_matches: 'Mais de um endereço casou com o texto: o primeiro foi usado.',
};

export const QUOTE_POINT_LABELS: Record<QuotePointKey, string> = {
  origin: 'Origem',
  destination: 'Destino',
  returnTo: 'Retorno',
};

// ── km: metros exatos → km cobrado com uma casa ─────────────────────────────

/** km cobrado em DÉCIMOS inteiros, meio para cima: 11.149 m → 111; 11.150 m → 112. */
export function kmTenthsFromMeters(meters: number): number {
  if (!Number.isSafeInteger(meters) || meters < 0) throw new Error(`invalid meters: ${meters}`);
  return Math.floor((meters + 50) / 100);
}

/** décimos inteiros → "11.1" (string decimal do wire, sem float) */
export function kmStringFromTenths(tenths: number): string {
  if (!Number.isSafeInteger(tenths) || tenths < 0) throw new Error(`invalid tenths: ${tenths}`);
  return `${Math.floor(tenths / 10)}.${tenths % 10}`;
}

/** metros → km cobrado ("11.1") — a regra ÚNICA da perna */
export function chargedKm(meters: number): string {
  return kmStringFromTenths(kmTenthsFromMeters(meters));
}

/** km digitado à mão: até 5 dígitos e 3 casas ("12.345"), com ponto */
const KM_INPUT_RE = /^(\d{1,5})(?:\.(\d{1,3}))?$/;
export const KmInput = z.string().regex(KM_INPUT_RE, 'km com até 3 casas');

/** "12.345" → 12345 metros, aritmética inteira */
export function metersFromKmInput(km: string): number {
  const m = KM_INPUT_RE.exec(km);
  if (!m) throw new Error(`invalid km: ${JSON.stringify(km)}`);
  return Number(m[1]) * 1000 + Number((m[2] ?? '').padEnd(3, '0'));
}

/** subtotal da perna em centavos: km cobrado × preço por km (regra dos lançamentos) */
export function legSubtotalCents(km: string, pricePerKm: string): number {
  return chargeTotalCents(km, pricePerKm);
}

// ── valor cheio do deslocamento (PoolTrack, 05/10) ──────────────────────────

/**
 * Duas formas de dar preço à cotação, um resultado só:
 *
 * - `per_km`: o preço por km de cada perna × o km dela; o total é a soma (o
 *   que existia antes);
 * - `full`: a pessoa digita o VALOR CHEIO do deslocamento (ida e volta) na
 *   linha do total. Esse valor é o total da cotação; o preço por km fica
 *   opcional e, se houver, só como referência ("pelo km daria R$ X").
 *
 * Toda cotação gravada antes de 05/10 é `per_km`.
 */
export const QUOTE_PRICING_MODES = ['per_km', 'full'] as const;
export type QuotePricingMode = (typeof QUOTE_PRICING_MODES)[number];

/**
 * Teto do valor cheio: R$ 100.000,00. É dinheiro digitado, sem recibo de
 * nada — o teto pega o zero a mais, e um deslocamento de verdade fica muito
 * abaixo dele.
 */
export const QUOTE_FULL_AMOUNT_MAX_CENTS = 10_000_000;

const MONEY_CENTS_RE = /^(\d+)(?:\.(\d{1,2}))?$/;

/** "1234.5" → 123450 (aritmética inteira); null se não for dinheiro sem sinal */
export function quoteMoneyCents(value: string): number | null {
  const m = MONEY_CENTS_RE.exec(value);
  if (!m) return null;
  const cents = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? cents : null;
}

/** o valor cheio no fio: dinheiro com até 2 casas, maior que zero e até o teto */
export const QuoteFullAmountSchema = z
  .string()
  .regex(MONEY_CENTS_RE, 'valor com até 2 casas')
  .refine((v) => (quoteMoneyCents(v) ?? 0) > 0, 'valor cheio maior que zero')
  .refine(
    (v) => (quoteMoneyCents(v) ?? Infinity) <= QUOTE_FULL_AMOUNT_MAX_CENTS,
    'valor cheio acima do teto'
  );

/**
 * O total da cotação na tela, pela regra única: com o valor cheio digitado,
 * ELE é o total, qualquer que seja a ordem em que a pessoa digitou — mudar o
 * preço por km depois não o sobrescreve, só muda a referência. Sem o valor
 * cheio, é a soma por km (null enquanto falta km ou preço de alguma perna).
 *
 * Os centavos de cada perna são null quando a perna ainda não tem km ou preço.
 */
export type QuotePricing =
  | { mode: 'per_km'; totalCents: number | null }
  | { mode: 'full'; totalCents: number; perKmCents: number | null };

export function quotePricing(input: {
  idaCents: number | null;
  voltaCents: number | null;
  /** o valor cheio digitado, em centavos; null = campo vazio */
  fullCents: number | null;
}): QuotePricing {
  const perKm =
    input.idaCents !== null && input.voltaCents !== null ? input.idaCents + input.voltaCents : null;
  if (input.fullCents !== null) {
    return { mode: 'full', totalCents: input.fullCents, perKmCents: perKm };
  }
  return { mode: 'per_km', totalCents: perKm };
}

/**
 * Perna acima disto pede conferência — NÃO bloqueia: um deslocamento longo
 * pode ser de verdade, mas é mais provável que um endereço tenha caído no
 * homônimo de outro estado.
 */
export const QUOTE_LONG_LEG_KM = 500;
export const QUOTE_LONG_LEG_WARNING = `Distância acima de ${QUOTE_LONG_LEG_KM} km — confira os endereços`;

/**
 * O km cobrado da perna ("500.1", o formato de `chargedKm`) passa do limite de
 * conferência? Em décimos inteiros, sem float: 500.0 não passa, 500.1 passa.
 * É aviso de tela: formato inesperado não derruba o diálogo, só não avisa.
 */
export function isLongLeg(km: string): boolean {
  const m = /^(\d+)(?:\.(\d))?$/.exec(km);
  if (!m) return false;
  return Number(m[1]) * 10 + Number(m[2] ?? '0') > QUOTE_LONG_LEG_KM * 10;
}

// ── polyline (algoritmo de polyline codificada do Google, precisão 5) ───────

/** "_p~iF~ps|U_ulLnnqC" → [[38.5, -120.2], [40.7, -120.95]] */
export function decodePolyline(encoded: string): Array<[number, number]> {
  const points: Array<[number, number]> = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  const next = (): number => {
    let result = 0;
    let shift = 0;
    let byte: number;
    do {
      if (index >= encoded.length) throw new Error('polyline truncada');
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    return result & 1 ? ~(result >> 1) : result >> 1;
  };
  while (index < encoded.length) {
    lat += next();
    lng += next();
    points.push([lat / 1e5, lng / 1e5]);
  }
  return points;
}

// ── link do Google Maps (não leva chave) ────────────────────────────────────

export interface LatLngLike {
  lat: number;
  lng: number;
}

/** um ponto por texto, com o id do lugar quando a pessoa escolheu uma sugestão */
export interface TextPlaceLike {
  text: string;
  placeId: string | null;
}

type DirectionsPoint = LatLngLike | TextPlaceLike | string;

/**
 * Rota no Google Maps para conferência: origem → destino → retorno. A URL de
 * "directions" do site não usa chave de API. Coordenada quando há; texto quando
 * a perna foi manual e o ponto nunca foi resolvido, ou quando a posição já foi
 * apagada pela retenção (termos do Google, F-112) — aí o id do lugar escolhido
 * vai junto (`*_place_id`), que o site do Google Maps aceita ao lado do texto.
 */
export function googleMapsDirectionsUrl(input: {
  origin: DirectionsPoint;
  destination: DirectionsPoint;
  returnTo: DirectionsPoint;
}): string {
  const asParam = (p: DirectionsPoint) =>
    typeof p === 'string' ? p : 'text' in p ? p.text : `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`;
  const placeOf = (p: DirectionsPoint) => (typeof p !== 'string' && 'text' in p ? p.placeId : null);
  const params = new URLSearchParams({
    api: '1',
    origin: asParam(input.origin),
    destination: asParam(input.returnTo),
    waypoints: asParam(input.destination),
    travelmode: 'driving',
  });
  const originPlace = placeOf(input.origin);
  const destinationPlace = placeOf(input.returnTo);
  const waypointPlace = placeOf(input.destination);
  if (originPlace) params.set('origin_place_id', originPlace);
  if (destinationPlace) params.set('destination_place_id', destinationPlace);
  if (waypointPlace) params.set('waypoint_place_ids', waypointPlace);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

// ── contrato: calcular (sem gravar) ─────────────────────────────────────────

const Lat = z.number().min(-90).max(90);
const Lng = z.number().min(-180).max(180);
const Money = z.string().regex(/^\d+(\.\d{1,2})?$/);

// ── autocompletar de endereço (Places API New, operador 05/10) ──────────────

/**
 * Token de SESSÃO do autocompletar. A documentação pede uma string base64
 * segura para URL e nome de arquivo e recomenda UUID v4; aqui é exatamente um
 * UUID v4 (`crypto.randomUUID()` no navegador). Uma sessão = as consultas de
 * UM campo até a escolha de uma sugestão; o Place Details com o mesmo token
 * encerra a sessão, e token reusado depois disso é cobrado como sem sessão.
 */
export const PLACE_SESSION_TOKEN_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
export const PlaceSessionTokenSchema = z.string().regex(PLACE_SESSION_TOKEN_RE);

/** id de lugar do Google (`ChIJ…`, `Ei…`): vai no CAMINHO do Place Details, então só base64url */
export const PLACE_ID_RE = /^[A-Za-z0-9_-]{4,1024}$/;
export const PlaceIdSchema = z.string().regex(PLACE_ID_RE);

/** abaixo disto o servidor nem consulta o provedor (cada consulta é cobrada) */
export const QUOTE_SUGGEST_MIN_CHARS = 3;
/** o Autocomplete (New) devolve no máximo cinco; o servidor corta em cinco de novo */
export const QUOTE_SUGGEST_MAX = 5;

/**
 * Um ponto para calcular:
 * - `address`: o TEXTO digitado (o servidor geocodifica);
 * - `pin`: a POSIÇÃO no mapa (sem geocodificar; com `reverse`, uma
 *   geocodificação reversa devolve o texto do endereço — é o fim de um arraste);
 * - `place`: uma sugestão ESCOLHIDA no autocompletar. O servidor resolve o lugar
 *   (Place Details com o token da sessão, que encerra a sessão) e nunca aceita
 *   coordenada do navegador para ele: o objeto é ESTRITO, e `lat`/`lng` a mais
 *   é 422, não um campo ignorado. `sessionToken: null` = o token já foi mandado
 *   uma vez (o navegador manda cada token uma vez só; ver takePlaceInput).
 */
export const QuotePointInputSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('address'), text: z.string().trim().min(3).max(300) }),
  z.object({ kind: z.literal('pin'), lat: Lat, lng: Lng, reverse: z.boolean().default(false) }),
  z.strictObject({
    kind: z.literal('place'),
    placeId: PlaceIdSchema,
    sessionToken: PlaceSessionTokenSchema.nullable(),
    /** o texto da sugestão escolhida (é o que fica no campo e vira o `input` gravado) */
    label: z.string().trim().min(3).max(300),
  }),
]);
export type QuotePointInput = z.infer<typeof QuotePointInputSchema>;
export type QuotePlacePointInput = Extract<QuotePointInput, { kind: 'place' }>;

/**
 * "Sugestões" de um campo de endereço. POST, e não GET com `?q=`: o logger de
 * requisições da API escreve a URL inteira, e o endereço digitado é dado
 * pessoal. `near` é a posição já resolvida de um ponto da cotação (o destino,
 * senão a origem): só ENVIESA as sugestões, nunca vira coordenada de ponto.
 */
export const QuoteSuggestBodySchema = z.object({
  input: z.string().max(200),
  sessionToken: PlaceSessionTokenSchema,
  near: z.object({ lat: Lat, lng: Lng }).nullable().default(null),
});
export type QuoteSuggestBody = z.input<typeof QuoteSuggestBodySchema>;

export const QuotePlaceSuggestionSchema = z.object({
  placeId: z.string(),
  /** o texto completo, que preenche o campo quando escolhido */
  label: z.string(),
  /** a linha de cima da sugestão (rua e número, ou o nome do lugar) */
  main: z.string(),
  /** a linha de baixo (bairro, cidade, UF); null quando o provedor não manda */
  secondary: z.string().nullable(),
});
export type QuotePlaceSuggestion = z.infer<typeof QuotePlaceSuggestionSchema>;

/**
 * `unavailable` = sem sugestões AGORA (provedor desligado, Places API não
 * habilitada na chave, cota, timeout): a tela some com a lista e segue com o
 * texto livre + geocodificação. Não é erro para a pessoa.
 *
 * `provider` decide a atribuição da lista: dado da Places API exibido fora de
 * um mapa do Google leva "Google Maps" (políticas da Places API); o falso não
 * leva, para não atribuir ao Google o que não é dele.
 */
export const QuoteSuggestResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ok'),
    provider: z.enum(['google', 'fake']),
    suggestions: z.array(QuotePlaceSuggestionSchema),
  }),
  z.object({ status: z.literal('unavailable') }),
]);
export type QuoteSuggestResponse = z.infer<typeof QuoteSuggestResponseSchema>;

/**
 * O token de sessão de UM campo, no navegador: nasce na primeira consulta, vale
 * para todas as consultas seguintes do campo, e é ENTREGUE na escolha — depois
 * dela (ou de limpar o campo) a próxima consulta nasce com um token novo. Puro,
 * para o teste provar o ciclo sem React.
 */
export function placeFieldSession(newToken: () => string) {
  let token: string | null = null;
  return {
    /** o token da consulta que vai sair agora */
    forQuery(): string {
      token ??= newToken();
      return token;
    },
    /** a escolha leva o token da sessão; o campo começa outra */
    pick(): string {
      const t = token ?? newToken();
      token = null;
      return t;
    },
    /** campo limpo: a sessão em curso é abandonada */
    reset(): void {
      token = null;
    },
  };
}

export interface QuotePlaceChoice {
  placeId: string;
  label: string;
  /** null = já foi mandado num "Calcular" */
  sessionToken: string | null;
}

/**
 * A escolha vira entrada do "Calcular" — e o token sai UMA vez: `rest` é o que
 * fica guardado no ponto, sem o token. Se o "Calcular" falhar e a pessoa tentar
 * de novo, o ponto vai sem token (o servidor resolve sem sessão, ou da memória
 * dele), e nenhuma réplica da API recebe o mesmo token duas vezes.
 */
export function takePlaceInput(choice: QuotePlaceChoice): {
  input: QuotePlacePointInput;
  rest: QuotePlaceChoice;
} {
  return {
    input: {
      kind: 'place',
      placeId: choice.placeId,
      sessionToken: choice.sessionToken,
      label: choice.label,
    },
    rest: { ...choice, sessionToken: null },
  };
}

export const QuoteCalculateBodySchema = z.object({
  origin: QuotePointInputSchema,
  destination: QuotePointInputSchema,
  /** null = a volta termina na origem */
  returnTo: QuotePointInputSchema.nullable(),
  /** as pernas a recalcular: o arraste do retorno só mexe na volta */
  legs: z
    .array(z.enum(QUOTE_LEGS))
    .min(1)
    .max(2)
    .default(['ida', 'volta'])
    .transform((legs) => [...new Set(legs)]),
});
export type QuoteCalculateBody = z.input<typeof QuoteCalculateBodySchema>;

export const QuoteResolvedPointSchema = z.object({
  lat: Lat,
  lng: Lng,
  /** texto do endereço; null = posição ecoada, sem consulta nesta chamada */
  address: z.string().nullable(),
  /** null = posição ecoada (o cliente já sabe a precisão dela) */
  precision: z.enum(GEO_PRECISIONS).nullable(),
  warnings: z.array(z.enum(QUOTE_POINT_WARNINGS)),
  /** true quando o servidor consultou o geocodificador por este ponto agora */
  resolved: z.boolean(),
});
export type QuoteResolvedPoint = z.infer<typeof QuoteResolvedPointSchema>;

export const QuoteLegRouteSchema = z.object({
  provider: z.enum(['google', 'fake']),
  meters: z.number().int().nonnegative(),
  seconds: z.number().int().nonnegative(),
  /** km cobrado, uma casa ("11.1") */
  km: z.string(),
  /** traçado para o mapa; vazio quando o provedor não devolveu */
  path: z.array(z.tuple([z.number(), z.number()])),
  /**
   * Recibo assinado pelo servidor (metros, segundos, pontos, provedor, ticket,
   * validade). O "Lançar" manda o recibo de volta e grava o que ELE diz — nunca
   * metros vindos do navegador.
   */
  receipt: z.string(),
});
export type QuoteLegRoute = z.infer<typeof QuoteLegRouteSchema>;

export const QuoteCalculateResponseSchema = z.object({
  points: z.object({
    origin: QuoteResolvedPointSchema,
    destination: QuoteResolvedPointSchema,
    returnTo: QuoteResolvedPointSchema.nullable(),
  }),
  legs: z.object({
    ida: QuoteLegRouteSchema.optional(),
    volta: QuoteLegRouteSchema.optional(),
  }),
});
export type QuoteCalculateResponse = z.infer<typeof QuoteCalculateResponseSchema>;

// ── contrato: lançar (grava a cotação e os lançamentos) ─────────────────────

export const QuoteSavedPointSchema = z.object({
  /** o que a pessoa digitou */
  input: z.string().trim().max(300).nullable(),
  /** o endereço encontrado (ou o da geocodificação reversa do arraste) */
  address: z.string().trim().max(400).nullable(),
  lat: Lat.nullable(),
  lng: Lng.nullable(),
  precision: z.enum(GEO_PRECISIONS).nullable(),
  warnings: z.array(z.enum(QUOTE_POINT_WARNINGS)).max(QUOTE_POINT_WARNINGS.length).default([]),
  /**
   * O id do lugar ESCOLHIDO no autocompletar e posicionado no "Calcular" (null
   * = texto livre, ou ponto arrastado depois). Os termos do Google deixam
   * guardar id de lugar sem prazo; a posição e o endereço, no máximo 30 dias
   * (F-112). Opcional no corpo: o SPA anterior não o manda.
   */
  placeId: PlaceIdSchema.nullable().default(null),
});
export type QuoteSavedPoint = z.infer<typeof QuoteSavedPointSchema>;

/**
 * Preço por km da perna. null = não informado: só vale com o valor cheio
 * (`fullAmount`); sem ele, o servidor exige o preço das duas pernas
 * (`quote_price_required`), como antes. O SPA anterior sempre manda o preço.
 */
const LegPrice = Money.nullable().default(null);

export const QuoteLegSaveSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('route'),
    receipt: z.string().min(10).max(8000),
    pricePerKm: LegPrice,
  }),
  z.object({
    mode: z.literal('manual'),
    /** km da perna, à mão (até 3 casas) */
    km: KmInput,
    reason: z.string().trim().min(5, 'motivo com 5+ caracteres').max(500),
    pricePerKm: LegPrice,
  }),
]);
export type QuoteLegSave = z.infer<typeof QuoteLegSaveSchema>;

export const QuoteCreateBodySchema = z.object({
  origin: QuoteSavedPointSchema,
  destination: QuoteSavedPointSchema,
  /** null = a volta termina na origem */
  returnTo: QuoteSavedPointSchema.nullable(),
  ida: QuoteLegSaveSchema,
  volta: QuoteLegSaveSchema,
  /**
   * O valor cheio do deslocamento (ida e volta), digitado na linha do total.
   * Presente = a cotação é `full`: ESTE é o total, e o "Lançar" cria UM
   * lançamento com ele. null = `per_km`, como antes (o SPA anterior não o manda).
   */
  fullAmount: QuoteFullAmountSchema.nullable().default(null),
});
export type QuoteCreateBody = z.input<typeof QuoteCreateBodySchema>;

// ── a cotação gravada ───────────────────────────────────────────────────────

export const QuotePointViewSchema = z.object({
  input: z.string().nullable(),
  /** null também depois da retenção do Google (`googlePurgedAt`) */
  address: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  precision: z.enum(GEO_PRECISIONS).nullable(),
  warnings: z.array(z.enum(QUOTE_POINT_WARNINGS)),
  /** o lugar escolhido no autocompletar; fica para sempre */
  placeId: z.string().nullable(),
});
export type QuotePointView = z.infer<typeof QuotePointViewSchema>;

export const QuoteLegViewSchema = z.object({
  provider: z.enum(QUOTE_LEG_PROVIDERS),
  meters: z.number().int(),
  seconds: z.number().int().nullable(),
  km: z.string(),
  /** null = não informado (só numa cotação com valor cheio) */
  pricePerKm: Money.nullable(),
  /** km × preço por km; numa cotação com valor cheio, só referência */
  subtotal: Money.nullable(),
  manualReason: z.string().nullable(),
  /**
   * a perna virou lançamento no "Lançar" (subtotal zero não vira; numa
   * cotação com valor cheio, nenhuma perna vira — o lançamento é um só)
   */
  charged: z.boolean(),
  /** null com `charged` = o lançamento foi excluído depois na aba Valores */
  chargeId: z.number().int().nullable(),
});
export type QuoteLegView = z.infer<typeof QuoteLegViewSchema>;

export const TicketQuoteSchema = z.object({
  id: z.number().int(),
  ticketNumber: z.number().int(),
  technician: z.object({ id: z.number().int(), name: z.string() }).nullable(),
  origin: QuotePointViewSchema,
  destination: QuotePointViewSchema,
  /** null = a volta terminou na origem */
  returnTo: QuotePointViewSchema.nullable(),
  ida: QuoteLegViewSchema,
  volta: QuoteLegViewSchema,
  /** como o total foi dado: soma por km, ou o valor cheio digitado */
  pricingMode: z.enum(QUOTE_PRICING_MODES),
  /** o valor cheio digitado (só em `full`; é o `total`) */
  fullAmount: Money.nullable(),
  /** o lançamento único do valor cheio; null em `full` = excluído depois na aba Valores */
  fullChargeId: z.number().int().nullable(),
  total: Money,
  createdBy: z.object({ id: z.number().int(), name: z.string() }).nullable(),
  createdAt: Dateish,
  /**
   * Quando a retenção apagou o que veio do Google (posições, endereços
   * encontrados, avisos e traçado). null = ainda tem, ou nunca teve (km à mão
   * sem ponto resolvido). O digitado, os ids de lugar, o km e o dinheiro ficam.
   */
  googlePurgedAt: Dateish.nullable(),
});
export type TicketQuote = z.infer<typeof TicketQuoteSchema>;

export const TicketQuoteListSchema = z.object({
  /** false = sem provedor configurado: só km à mão, com motivo */
  mapsAvailable: z.boolean(),
  /**
   * true = esta API grava o valor cheio (`fullAmount`). O SPA só mostra o campo
   * com este sinal: o SPA publica antes da API, e a API anterior DESCARTARIA o
   * `fullAmount` em silêncio e lançaria por km (mudanca-de-dois-lados.md).
   */
  fullAmountAvailable: z.boolean(),
  items: z.array(TicketQuoteSchema),
});
export type TicketQuoteList = z.infer<typeof TicketQuoteListSchema>;

/**
 * O diálogo mostra o campo do valor cheio? Só quando a lista da API diz, com
 * `true` de verdade, que grava o valor cheio. A API anterior não manda o campo:
 * aí o diálogo é o por km de antes, sem o campo (e nada é descartado calado).
 */
export function quoteFullAmountEnabled(
  list: { fullAmountAvailable?: unknown } | undefined
): boolean {
  return list?.fullAmountAvailable === true;
}

// ── termos do Google Maps Platform (F-112, operador 05/10) ──────────────────

/**
 * Os termos específicos dos serviços deixam guardar latitude/longitude do
 * Geocoding por no máximo 30 dias corridos (id de lugar, sem prazo). A
 * retenção da API apaga antes disso — ver billing/domain/quote-retention.
 */
export const GOOGLE_LATLNG_CACHE_DAYS = 30;

/**
 * O mini mapa da cotação é um mapa do Google (Maps JavaScript API): conteúdo do
 * Geocoding, do Places e do Routes não pode aparecer num mapa de outro
 * fornecedor. A chave do NAVEGADOR (restrita por referrer a erp.fatorh.ai e só
 * à Maps JavaScript API) vem da API em tempo de execução, nunca do build.
 *
 * `browserKey: null` = sem mapa: o diálogo segue com endereços, Calcular, km,
 * preço e Lançar. `mapId` liga os marcadores avançados (AdvancedMarkerElement);
 * sem ele o mapa usa o marcador clássico.
 */
export const QuoteMapConfigSchema = z.object({
  browserKey: z.string().nullable(),
  mapId: z.string().nullable(),
});
export type QuoteMapConfig = z.infer<typeof QuoteMapConfigSchema>;

/** o texto de um ponto gravado: o digitado; senão o encontrado; senão a posição */
export function quotePointText(p: QuotePointView): string {
  if (p.input) return p.input;
  if (p.address) return p.address;
  if (p.lat !== null && p.lng !== null) return `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`;
  return '—';
}

/**
 * "Ver rota no Google Maps" de uma cotação gravada. Com as posições, por
 * coordenada; depois da retenção (ou num ponto nunca resolvido), pelo texto
 * digitado e o id do lugar escolhido. null = falta texto e posição de algum
 * ponto (não há rota a mostrar).
 */
export function quoteDirectionsUrl(
  q: Pick<TicketQuote, 'origin' | 'destination' | 'returnTo'>
): string | null {
  const asPoint = (p: QuotePointView): DirectionsPoint | null => {
    if (p.lat !== null && p.lng !== null) return { lat: p.lat, lng: p.lng };
    const text = p.input?.trim() ?? '';
    return text === '' ? null : { text, placeId: p.placeId };
  };
  const origin = asPoint(q.origin);
  const destination = asPoint(q.destination);
  const returnTo = q.returnTo ? asPoint(q.returnTo) : origin;
  if (!origin || !destination || !returnTo) return null;
  return googleMapsDirectionsUrl({ origin, destination, returnTo });
}
